/**
 * Bucket daily usage series into averaged chart points for longer ranges.
 *
 * - 30d → 10 points, each the mean of a 3-day triplet (30 days total)
 * - 90d → 12 points, each the mean of a 7-day week  (84 days total)
 */

export type MetricKey = 'messages' | 'sessions' | 'files';

export type DailyPerUser = {
  userId: string;
  messages: number;
  sessions: number;
  files: number;
};

export type DailySeriesPoint = {
  date: string;
  perUser: DailyPerUser[];
};

export type AggregatedSeriesPoint = {
  /** End date of the bucket — used as the x-axis key */
  date: string;
  dateStart: string;
  dateEnd: string;
  bucketSize: number;
  perUser: DailyPerUser[];
};

export type SeriesBucketConfig = {
  buckets: number;
  daysPerBucket: number;
};

export const RANGE_AGGREGATION: Record<'7d' | '30d' | '90d', SeriesBucketConfig | null> = {
  '7d': null,
  '30d': { buckets: 10, daysPerBucket: 3 },
  '90d': { buckets: 12, daysPerBucket: 7 },
};

/** Arithmetic mean; returns 0 for an empty input. */
export function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, n) => sum + n, 0) / values.length;
}

function averagePerUser(
  chunk: DailySeriesPoint[],
  userIds: string[]
): DailyPerUser[] {
  return userIds.map(userId => {
    const messages: number[] = [];
    const sessions: number[] = [];
    const files: number[] = [];

    for (const day of chunk) {
      const row = day.perUser.find(u => u.userId === userId);
      messages.push(row?.messages ?? 0);
      sessions.push(row?.sessions ?? 0);
      files.push(row?.files ?? 0);
    }

    return {
      userId,
      messages: average(messages),
      sessions: average(sessions),
      files: average(files),
    };
  });
}

/**
 * Collapse consecutive daily points into `buckets` averaged groups of
 * `daysPerBucket` days each. Uses the trailing `buckets * daysPerBucket` days.
 */
export function aggregateDailySeries(
  series: DailySeriesPoint[],
  { buckets, daysPerBucket }: SeriesBucketConfig
): AggregatedSeriesPoint[] {
  const windowDays = buckets * daysPerBucket;
  const days = series.slice(-windowDays);

  const userIds = [
    ...new Set(days.flatMap(day => day.perUser.map(u => u.userId))),
  ];

  const result: AggregatedSeriesPoint[] = [];

  for (let b = 0; b < buckets; b++) {
    const startIdx = b * daysPerBucket;
    const chunk = days.slice(startIdx, startIdx + daysPerBucket);

    const dateStart = chunk[0]?.date ?? '';
    const dateEnd = chunk[chunk.length - 1]?.date ?? dateStart;

    result.push({
      date: dateEnd,
      dateStart,
      dateEnd,
      bucketSize: chunk.length,
      perUser: averagePerUser(chunk, userIds),
    });
  }

  return result;
}

/** Apply range-specific aggregation; 7d returns daily points unchanged. */
export function seriesForChartRange(
  series: DailySeriesPoint[],
  rangeKey: '7d' | '30d' | '90d'
): AggregatedSeriesPoint[] {
  const config = RANGE_AGGREGATION[rangeKey];
  if (!config) {
    return series.map(day => ({
      date: day.date,
      dateStart: day.date,
      dateEnd: day.date,
      bucketSize: 1,
      perUser: day.perUser,
    }));
  }
  return aggregateDailySeries(series, config);
}

export function formatBucketRangeLabel(
  dateStart: string,
  dateEnd: string,
  bucketSize: number
): string {
  try {
    const start = new Date(`${dateStart}T00:00:00.000Z`);
    const end = new Date(`${dateEnd}T00:00:00.000Z`);
    const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    const sameDay = dateStart === dateEnd;
    const range = sameDay ? fmt.format(end) : `${fmt.format(start)} – ${fmt.format(end)}`;
    const unit = bucketSize === 7 ? 'week' : bucketSize === 3 ? '3-day' : `${bucketSize}-day`;
    return `${range} (${unit} avg)`;
  } catch {
    return dateEnd;
  }
}
